import { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft, Check, PartyPopper, PaintRoller } from 'lucide-react';
import Seo from '../components/Seo';
import { MaskedLines, Eyebrow } from '../components/motion';
import { useLang, useT } from '../i18n/LangContext';
import { useContent } from '../data/useContent';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function OptionCard({ selected, onClick, label, sub, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`flex w-full items-center justify-between rounded-lg border p-5 text-left transition-[border-color,background-color] duration-300 ${selected ? 'border-gold bg-gold/10' : 'border-black/10 bg-white hover:border-gold/60'}`}>
      <span>
        <span className="block text-base font-bold text-ink">{label}</span>
        {sub && <span className="mt-0.5 block text-xs text-ink/55">{sub}</span>}
      </span>
      <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors ${selected ? 'border-gold bg-gold text-ink' : 'border-ink/20'}`}>
        {selected && <Check className="h-4 w-4" />}
      </span>
    </button>
  );
}

export default function QuotePage() {
  const t = useT();
  const { lang } = useLang();
  const { SERVICES } = useContent();
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const panelRef = useRef(null);
  const [data, setData] = useState({
    service: params.get('service') || '', project_type: '', rooms: '', sqft: '',
    timeline: '', name: '', email: '', phone: '', address: '', date: '', time: '',
  });
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  useEffect(() => {
    if (step > 0 && panelRef.current) {
      const y = panelRef.current.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    }
  }, [step]);

  const STEP_LABELS = [t['wz.s1'], t['wz.s2'], t['wz.s3'], t['wz.s4'], t['wz.s5']];
  const TIMES = [t['qf.t1'], t['qf.t2'], t['qf.t3'], t['qf.t4']];
  const TYPES = [
    { v: 'Interior', l: t['wz.interior'] },
    { v: 'Exterior', l: t['wz.exterior'] },
    { v: 'Both', l: t['wz.both'] },
  ];
  const TIMELINES = [
    { v: 'As soon as possible', l: t['wz.tl1'] },
    { v: 'In the next 2–4 weeks', l: t['wz.tl2'] },
    { v: 'In 1–2 months', l: t['wz.tl3'] },
    { v: "I'm flexible", l: t['wz.tl4'] },
  ];

  const valid = [
    !!data.service,
    !!data.project_type,
    !!(data.rooms || data.sqft),
    !!data.timeline,
    !!(data.name && data.email && data.phone),
  ];

  const submit = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, { ...data, source: 'quote-wizard', lang });
      localStorage.setItem('rev-quote-submitted', '1');
      setDone(true);
      window.scrollTo(0, 0);
    } catch {
      toast.error(t['qf.toastErr']);
    }
    setSending(false);
  };

  const paintPct = done ? 100 : Math.max(6, (step / 5) * 100);

  return (
    <>
      <Seo title={t['seo.quote.title']} description={t['seo.quote.desc']} />
      <section data-testid="quote-wizard" className="grain relative overflow-hidden bg-[#F3EDE0] pb-28 pt-16 md:pt-24">
        {/* Gold paint stroke filling the wall as steps progress */}
        <motion.div aria-hidden className="absolute inset-y-0 left-0"
          animate={{ width: `${paintPct}%` }}
          transition={{ duration: 1.3, ease: [0.16, 1, 0.3, 1] }}
          style={{ background: 'repeating-linear-gradient(90deg, #C9A227 0px, #D3AC2E 7px, #C9A227 14px)' }} />
        {/* Wet edge */}
        <motion.div aria-hidden className="absolute inset-y-0 z-10 w-1.5 bg-gold-dim/80"
          animate={{ left: `${paintPct}%` }}
          transition={{ duration: 1.3, ease: [0.16, 1, 0.3, 1] }} style={{ transform: 'translateX(-3px)' }} />
        {/* Roller */}
        <motion.div aria-hidden key={`roller-${step}-${done}`} className="absolute z-10"
          initial={{ top: '6%' }}
          animate={{
            left: `${paintPct}%`,
            top: ['6%', '72%', '38%'],
          }}
          transition={{ left: { duration: 1.3, ease: [0.16, 1, 0.3, 1] }, top: { duration: 1.3, ease: 'easeInOut' } }}
          style={{ transform: 'translateX(-26px)' }}>
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white shadow-[0_6px_20px_rgba(0,0,0,0.25)] ring-2 ring-gold/50">
            <PaintRoller className="h-7 w-7 text-gold-dim" strokeWidth={2} />
          </span>
        </motion.div>

        <div className="container-site relative z-20 max-w-3xl">
          <div ref={panelRef} className="rounded-2xl border border-black/10 bg-white/90 p-6 text-ink shadow-2xl backdrop-blur-md md:p-10">
          <Eyebrow dark>{t['wz.eyebrow']}</Eyebrow>
          <MaskedLines as="h1" className="mt-4 font-serif text-4xl font-semibold leading-[1.02] tracking-tight sm:text-5xl"
            lines={[{ text: t['wz.h1a'], cls: 'text-ink' }, { text: t['wz.h1b'], cls: 'italic text-gold-dim' }]} />

          {done ? (
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} data-testid="wizard-success"
              className="mt-14 rounded-2xl border border-gold/40 bg-[#FBF7EC] p-10 text-center">
              <PartyPopper className="mx-auto h-10 w-10 text-gold-dim" />
              <h2 className="mt-5 font-serif text-3xl font-semibold text-ink">{t['wz.successTitle']} {data.name.split(' ')[0]}.</h2>
              <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-ink/60">
                {t['wz.successSub1']} <strong className="text-ink">{data.email}</strong>{t['wz.successSub2']}
              </p>
              <p className="mt-6 text-xs uppercase tracking-[0.2em] text-gold-dim">{t['wz.sooner']} 613-859-5893</p>
            </motion.div>
          ) : (
            <>
              <div className="mt-12" data-testid="wizard-progress">
                <div className="flex justify-between text-[10px] font-bold uppercase tracking-[0.18em] text-ink/40">
                  {STEP_LABELS.map((l, i) => (
                    <span key={l} className={i <= step ? 'text-gold-dim' : ''}>{l}</span>
                  ))}
                </div>
                <div className="mt-3 h-1 overflow-hidden rounded-full bg-black/10">
                  <motion.div className="h-full bg-gold" animate={{ width: `${((step + 1) / 5) * 100}%` }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }} />
                </div>
              </div>

              <AnimatePresence mode="wait">
                <motion.div key={step} initial={{ opacity: 0, x: 32 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -32 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }} className="mt-10">

                  {step === 0 && (
                    <div className="space-y-3" data-testid="wizard-step-service">
                      <h2 className="mb-5 font-serif text-2xl font-semibold text-ink">{t['wz.q1']}</h2>
                      {SERVICES.map((s) => (
                        <OptionCard key={s.slug} testid={`wizard-service-${s.slug}`} label={s.name} sub={s.tagline.slice(0, 70) + '…'}
                          selected={data.service === s.name} onClick={() => set('service', s.name)} />
                      ))}
                    </div>
                  )}

                  {step === 1 && (
                    <div className="space-y-3" data-testid="wizard-step-type">
                      <h2 className="mb-5 font-serif text-2xl font-semibold text-ink">{t['wz.q2']}</h2>
                      {TYPES.map((o) => (
                        <OptionCard key={o.v} testid={`wizard-type-${o.v.toLowerCase()}`} label={o.l} selected={data.project_type === o.v} onClick={() => set('project_type', o.v)} />
                      ))}
                    </div>
                  )}

                  {step === 2 && (
                    <div data-testid="wizard-step-details">
                      <h2 className="mb-5 font-serif text-2xl font-semibold text-ink">{t['wz.q3']}</h2>
                      <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-ink/55">{t['wz.roomsLabel']}</label>
                      <select data-testid="wizard-rooms-select" value={data.rooms} onChange={(e) => set('rooms', e.target.value)} className="input-light mb-5" aria-label={t['wz.roomsLabel']}>
                        <option value="">{t['wz.roomsSelect']}</option>
                        {['1', '2', '3', '4', '5', '6+'].map((r) => <option key={r} value={r}>{r}</option>)}
                        <option value="Whole home / building">{t['wz.roomsWhole']}</option>
                      </select>
                      <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-ink/55">{t['wz.sqftLabel']}</label>
                      <input data-testid="wizard-sqft-input" type="text" inputMode="numeric" placeholder={t['wz.sqftPh']} value={data.sqft}
                        onChange={(e) => set('sqft', e.target.value)} className="input-light" aria-label={t['wz.sqftLabel']} />
                      <p className="mt-3 text-xs text-ink/50">{t['wz.either']}</p>
                    </div>
                  )}

                  {step === 3 && (
                    <div className="space-y-3" data-testid="wizard-step-timeline">
                      <h2 className="mb-5 font-serif text-2xl font-semibold text-ink">{t['wz.q4']}</h2>
                      {TIMELINES.map((o) => (
                        <OptionCard key={o.v} testid={`wizard-timeline-${o.v.toLowerCase().replace(/[^a-z0-9]/g, '-')}`} label={o.l} selected={data.timeline === o.v} onClick={() => set('timeline', o.v)} />
                      ))}
                    </div>
                  )}

                  {step === 4 && (
                    <div data-testid="wizard-step-contact">
                      <h2 className="mb-5 font-serif text-2xl font-semibold text-ink">{t['wz.q5']}</h2>
                      <div className="space-y-3">
                        <input data-testid="wizard-name-input" required placeholder={t['qf.name']} value={data.name} onChange={(e) => set('name', e.target.value)} className="input-light" aria-label={t['qf.name']} />
                        <div className="grid gap-3 sm:grid-cols-2">
                          <input data-testid="wizard-email-input" required type="email" placeholder={t['qf.email']} value={data.email} onChange={(e) => set('email', e.target.value)} className="input-light" aria-label={t['qf.email']} />
                          <input data-testid="wizard-phone-input" required type="tel" placeholder={t['qf.phone']} value={data.phone} onChange={(e) => set('phone', e.target.value)} className="input-light" aria-label={t['qf.phone']} />
                        </div>
                        <input data-testid="wizard-address-input" placeholder={t['wz.address']} value={data.address} onChange={(e) => set('address', e.target.value)} className="input-light" aria-label={t['wz.address']} />
                        <div className="grid gap-3 sm:grid-cols-2">
                          <input data-testid="wizard-date-input" type="date" value={data.date} onChange={(e) => set('date', e.target.value)} className="input-light" aria-label="Preferred date" />
                          <select data-testid="wizard-time-select" value={data.time} onChange={(e) => set('time', e.target.value)} className="input-light" aria-label={t['wz.prefTime']}>
                            <option value="">{t['wz.prefTime']}</option>
                            {TIMES.map((tm) => <option key={tm} value={tm}>{tm}</option>)}
                          </select>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              <div className="mt-10 flex items-center justify-between">
                <button data-testid="wizard-back-btn" onClick={() => setStep(step - 1)} disabled={step === 0}
                  className="ghost-btn-light disabled:invisible"><ArrowLeft className="h-4 w-4" /> {t['wz.back']}</button>
                {step < 4 ? (
                  <button data-testid="wizard-next-btn" onClick={() => valid[step] && setStep(step + 1)} disabled={!valid[step]}
                    className="gold-btn disabled:cursor-not-allowed disabled:opacity-40">{t['wz.next']} <ArrowRight className="h-4 w-4" /></button>
                ) : (
                  <button data-testid="wizard-submit-btn" onClick={submit} disabled={!valid[4] || sending}
                    className="gold-btn disabled:cursor-not-allowed disabled:opacity-40">
                    {sending ? t['wz.sending'] : <>{t['wz.submit']} <ArrowRight className="h-4 w-4" /></>}
                  </button>
                )}
              </div>
              <p className="mt-6 text-center text-[11px] text-ink/45">{t['wz.fine']}</p>
            </>
          )}
          </div>
        </div>
      </section>
    </>
  );
}
